<template>
  <new-bs-box
    :disabled="loading || disabled || null"
    :bs="{
      ...defaultBs,
      ...bs,
      '.btn-content': contentStyle,
    }"
    as="button"
    @click="onClick"
  >
    <BeerWidgetLoading
      v-if="loading"
      :color="`palette.${modifier}.disable`"
      text=""
    />
    <BeerIcon
      v-if="iconBefore || icon"
      :icon="iconBefore || icon"
      :bs="{ mr: 4, ...iconBs }"
      :color="null"
      :size="size === 'l' ? 16: 14"
      :text-inline="false"
    />
    <span
      class="btn-content"
    >
      <template v-if="variant === 'link'">
        <BeerText
          :bs="maxContent ? {my: 3} : {}"
          :disabled="loading || disabled|| null"
          variant="link"
        >
          <!-- @slot 按钮内容插槽 -->
          <slot />
        </BeerText>
      </template>
      <template v-else><slot /></template>
    </span>
    <BeerIcon
      v-if="iconAfter || iconRight"
      :icon="iconAfter || iconRight"
      :text-inline="false"
      :bs="{
        ml: 8,
        ...iconBs,
      }"
      :color="null"
      :size="size === 'l' ? 16: 14"
    />
  </new-bs-box>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue'
  import { BeerStyleObj, ModifierColor } from '@xhs/yam-beer/core'
  import BeerWidgetLoading from '../Spinner/WidgetLoading.vue'
  import BeerText from '../Text/Text.vue'
  import BeerIcon from '../Icon/Icon.vue'
  import initStyle from './style'
  import NewBsBox from '../../primitive/NewBsBox'

  export type TypeSize = 'nss' | 'nm' | 's' | 'm' | 'l'
  export type TypeVariant = 'secondary' | 'outline' | 'default' | 'text' | 'stroke' | 'link'

  initStyle()

  export default defineComponent({
    name: 'BeerButton',
    components: {
      BeerText,
      NewBsBox,
      BeerIcon,
      BeerWidgetLoading,
    },
    props: {
      /**
       * 样式的类型，整体样式风格，如填充、边框、文字
       * stroke 值弃用了，样式合并入了 text
       *
       * 与 AntD 的 type 类似
       * @type {default|secondary|outline|text}
       */
      variant: {
        type: String as PropType<TypeVariant>,
        default: 'default',
      },
      /**
       * 意图颜色，用来呈现按钮的意图提示
       * 在 variant  = link 的时候无效
       * @type {primary|success|danger|warn|secondary}
       */
      modifier: {
        type: String as PropType<ModifierColor>,
        default: 'primary',
      },
      /**
       * small，meidum，large
       * @type {s|m|l}
       */
      size: {
        type: String as PropType<TypeSize>,
        default: 'm',
      },
      /**
       * 是否禁用
       * @type {true|false}
       */
      disabled: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false,
      },
      /**
       * 左侧 icon name
       * @version 4.1.0
       */
      iconBefore: {
        type: String as PropType<string>,
        required: false,
      },
      /**
       * 右侧 icon name
       * @version 4.1.0
       */
      iconAfter: {
        type: String as PropType<string>,
        required: false,
      },
      /**
       * 设置按钮里文字部分的最大宽度的像素值
       * @version 4.1.0
       */
      maxContentWidth: {
        type: Number as PropType<number>,
        required: false,
      },
      /**
       * 使用 iconBefore
       * @deprecated 4.1.0
       */
      icon: {
        type: String as PropType<string>,
        required: false,
      },
      /**
       * 设置按钮载入状态，同时会 disabled 掉按钮交互
       */
      loading: {
        type: Boolean,
        default: false,
      },
      /**
       * 使用 iconAfter
       * @deprecated 4.1.0
       */
      iconRight: {
        type: String as PropType<string>,
        required: false,
      },
      /**
       * 使用 maxContentWidth
       * @deprecated  4.1.0
       */
      maxContent: {
        type: Number as PropType<number>,
        required: false,
      },
      /**
       * 使用 @click 方法
       * @deprecated  4.1.0
       */
      handleClick: {
        type: Function,
        required: false,
      },
      /**
       * @ignore
       * 容器 bs style
       * @see style
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({ }),
      },
      /**
       * @ignore
       * 左右 icon 的 inline 样式
       * @see style
       */
      iconBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({ }),
      },
    },
    data() {
      return {
        defaultBs: {
          'cmp.button.base': 'default',
          'cmp.button.variant': true,
          'cmp.button.size': true,
        },
      }
    },
    computed: {
      contentStyle(): BeerStyleObj {
        if (!(this.maxContent || this.maxContentWidth)) {
          return {}
        }
        return {
          'cmp.button.base': 'content',
          maxWidth: this.maxContentWidth || this.maxContent,
        }
      },
    },
    methods: {
      onClick(e: Event) {
        if (this.handleClick) {
          this.handleClick(e)
        }
      },
    },
  })
</script>
